import { Language } from "@faire/web-api/indigofair/languages/Language";
import { ILocaleKey } from "@faire/web-api/indigofair/locale/ILocaleKey";
import { getGlobalProperty } from "@faire/web/common/globals/getGlobalProperty";
import { getCurrentPath } from "@faire/web/common/Path";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { ILanguageSelectorProps } from "@faire/web/ui/LanguageSelector/common";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { Suspense } from "react";

import { HeaderAccountDropdown } from "@faire/retailer/components/Header/desktop/HeaderAccountDropdown";
import { LazyHeaderLanguageSelector } from "@faire/retailer/components/Header/desktop/HeaderLanguageSelector/LazyHeaderLanguageSelector";
import { HeaderNotificationsDropdown } from "@faire/retailer/components/Header/HeaderNotifications/HeaderNotificationsDropdown";
import {
  HeaderButton,
  HeaderLine,
  HeaderLinkButton,
} from "@faire/retailer/components/Header/styled";
import { LazyLanguageSelector } from "@faire/retailer/components/LanguageSelector/LazyLanguageSelector";
import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { getIsRobot } from "@faire/retailer/serialized-data/getIsRobot";
import { getSessionCountry } from "@faire/retailer/serialized-data/getSessionCountry";
import { getSettingEnableRetailerNotification } from "@faire/retailer/settings/getSettingEnableRetailerNotification";
import { useSettingGlobalNavigationRedesign } from "@faire/retailer/settings/useSettingGlobalNavigationRedesign";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { useUserStore } from "@faire/retailer/stores/domain/UserStore";

import { useNavigateAndHideMenu } from "../mobile/Navigation/useNavigateAndHideMenu";

export const LoginSection: React.FC<{ signUpCTAText?: string }> = React.memo(
  ({ signUpCTAText }) => {
    const isLoggedInRetailer = useIsLoggedInRetailer();
    const { showLanguageSelector } = useRetailerStore(["showLanguageSelector"]);
    const { browserLevelLanguage, browserLevelLocaleKey } = useUserStore([
      "browserLevelLanguage",
      "browserLevelLocaleKey",
    ]);

    const availableLanguageSelectorLocales =
      getGlobalProperty<ILocaleKey[]>("availableLanguageSelectorLocales") ?? [];

    return isLoggedInRetailer ? (
      <>
        {showLanguageSelector ? (
          <LanguageSelectorSection
            eventKey="logged-in-top"
            browserLanguage={browserLevelLanguage as Language}
            browserLocale={browserLevelLocaleKey}
            availableLocales={availableLanguageSelectorLocales}
            assignLocalizedUrl
            showCountries={getIsRobot()}
            sessionCountry={getSessionCountry()}
          />
        ) : null}
        {getSettingEnableRetailerNotification() ? (
          <HeaderNotificationsDropdown />
        ) : null}
        <HeaderAccountDropdown />
      </>
    ) : (
      <>
        <LoginLink />
        <SignUpLink signUpCTAText={signUpCTAText} />
      </>
    );
  }
);

const LoginLink = () => {
  const { navigateToSignIn } = useNavigateAndHideMenu();

  return (
    <HeaderLinkButton onClick={() => navigateToSignIn()} data-test-id="login">
      <StrictLocalMsg
        defaultMessage="Sign in"
        description={{
          text: "title of link that leads to the sign in page",
        }}
      />
    </HeaderLinkButton>
  );
};

const LanguageSelectorSection = (props: ILanguageSelectorProps) => {
  const shouldUseRedesignExperimentSpacing =
    useSettingGlobalNavigationRedesign();

  return (
    <>
      <Suspense fallback={null}>
        {shouldUseRedesignExperimentSpacing ? (
          <LazyHeaderLanguageSelector {...props} />
        ) : (
          <LazyLanguageSelector {...props} />
        )}
      </Suspense>
      <HeaderLine />
    </>
  );
};

const SignUpLink: React.FC<{ signUpCTAText?: string }> = ({
  signUpCTAText,
}) => {
  const isSSR = useIsSSR();
  const { navigateToSignUp } = useNavigateAndHideMenu();

  return (
    <HeaderButton
      onClick={() =>
        navigateToSignUp(undefined, {
          sourceId: "desktop_catnav_signup_cta",
        })
      }
      // We set a link for graceful degradation in SSR.
      linkTo={
        isSSR
          ? getCurrentPath()
              .replace({
                signUp: "1",
              })
              .toString()
          : undefined
      }
      data-test-id="signUpButton"
    >
      {signUpCTAText ? (
        signUpCTAText
      ) : (
        <StrictLocalMsg defaultMessage="Sign up to shop" />
      )}
    </HeaderButton>
  );
};
